import { useState } from "react";
import { Key, Hourglass, Zap, ArrowRight, X } from "lucide-react";

/**
 * 3-slide onboarding modal — introduces the three main services (Location,
 * Pack Heures, Trigo Express). Fully controlled by the caller via the `open`
 * prop; onClose is called when the user finishes or dismisses the tutorial.
 * The caller decides when to show it (e.g. on-demand from Profil).
 */
const SLIDES = [
  {
    id: "location",
    icon: Key,
    accent: "#FACC15",
    tag: "Service 1 · Location",
    title: "Louez avec chauffeur",
    body:
      "À l'heure, à la journée, en tour de ville ou en sortie de ville — choisissez le rythme qui vous convient et laissez-vous conduire.",
    bgClass: "from-[#0A2540] to-[#0F2E4E]",
  },
  {
    id: "packs",
    icon: Hourglass,
    accent: "#FACC15",
    tag: "Service 2 · Pack Heures",
    title: "Achetez des forfaits d'heures",
    body:
      "Des packs valables de 3 à 30 jours qui vous font économiser jusqu'à 20 000 FCFA. Idéal pour les rendez-vous quotidiens.",
    bgClass: "from-[#0F2E4E] to-[#0A2540]",
  },
  {
    id: "express",
    icon: Zap,
    accent: "#FACC15",
    tag: "Service 3 · Trigo Express",
    title: "Une course VTC à la demande",
    body:
      "Commandez un chauffeur en quelques secondes — moto, berline ou confort. Suivi en temps réel jusqu'à la destination.",
    bgClass: "from-[#0A2540] to-[#051426]",
  },
];

export default function OnboardingModal({ open, onClose }) {
  const [index, setIndex] = useState(0);
  if (!open) return null;

  const slide = SLIDES[index];
  const isLast = index === SLIDES.length - 1;
  const Icon = slide.icon;

  const handleNext = () => {
    if (isLast) {
      // Reset for next open
      setIndex(0);
      onClose?.();
      return;
    }
    setIndex((i) => Math.min(i + 1, SLIDES.length - 1));
  };

  const handleSkip = () => {
    setIndex(0);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 sm:p-8"
      style={{ fontFamily: "Outfit, Inter, sans-serif" }}
      data-testid="onboarding-modal"
    >
      <div
        className={`relative w-full max-w-md rounded-3xl overflow-hidden bg-gradient-to-br ${slide.bgClass} text-white shadow-2xl`}
        data-testid={`onboarding-slide-${slide.id}`}
      >
        {/* Skip button (top-right) */}
        <button
          type="button"
          onClick={handleSkip}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          aria-label="Passer"
          data-testid="onboarding-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Visual */}
        <div className="pt-12 pb-6 px-8 flex flex-col items-center text-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 rounded-full bg-[#FACC15]/25 blur-2xl scale-150" />
            <div className="absolute inset-0 rounded-full bg-[#FACC15]/20 animate-pulse" />
            <div className="relative w-24 h-24 rounded-full bg-[#FACC15] flex items-center justify-center">
              <Icon className="w-11 h-11 text-[#0A2540]" strokeWidth={2.2} />
            </div>
          </div>

          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FACC15]">
            {slide.tag}
          </p>
          <h2
            className="mt-2 text-3xl font-extrabold leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            {slide.title}
          </h2>
          <p className="mt-4 text-sm text-white/80 leading-relaxed max-w-sm">
            {slide.body}
          </p>
        </div>

        {/* Footer: dots + CTAs */}
        <div className="px-8 pb-8 pt-2 bg-black/10">
          <div className="flex justify-center gap-1.5 mb-5" data-testid="onboarding-progress">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-8 bg-[#FACC15]" : "w-2 bg-white/30 hover:bg-white/50"
                }`}
                aria-label={`Aller au slide ${i + 1}`}
                data-testid={`onboarding-dot-${i}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            {!isLast && (
              <button
                type="button"
                onClick={handleSkip}
                className="text-sm font-semibold text-white/70 hover:text-white transition h-12 px-4"
                data-testid="onboarding-skip"
              >
                Passer
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              className="ml-auto inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FACC15] hover:bg-[#EAB308] text-[#0A2540] font-extrabold text-sm shadow-lg shadow-[#FACC15]/25 transition"
              style={{ fontFamily: "Outfit" }}
              data-testid="onboarding-next"
            >
              {isLast ? "C'est parti" : "Suivant"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
